// ============================================================================
// NEXT STEPS ON A MAIL CARD (Day page, Lane 6b) — pure words and rules, so a test can read them.
//
// The Worker proposes ahead of time (Lane 6a, in the sync cron after triage); this file decides
// how a proposal reads and what an Accept does on the page. Nothing here is applied on its own:
// every write is an owner's Accept, and every accept writes to LifeOS only.
//
// THE PANEL NEVER HIDES KNOWLEDGE: a proposal's `detail` is always on its row (a skipped row
// collapses to one line, by the brief), and the provenance line says what Claude read.
// ============================================================================

import { addDays, formatShortDate, type ISODate } from '@app/shared'

import { localDayOf } from '../../lib/time'
import type { MailProposal, MailProposalKind, MailTriage, MailTriageItem } from '../../lib/data/types'
import { clock12, clock24, dayLabel } from './designed'

/** The row's state after Accept, in the brief's words. */
export type AcceptedWord = 'Added' | 'Done' | 'Opened' | 'Copied' | 'Noted'

export interface AcceptPlan {
  /** The undo toast's verb ("Added to calendar · Undo"). Null = no toast. */
  toast: string | null
  /** Accept is reversible with `undoMailProposal`, offered in the toast. */
  undo: boolean
  /** The card leaves its pile the way Done does (mark_done). */
  leaves: boolean
  /** A URL to open in a new tab, synchronously in the click (popup blockers). */
  open: string | null
  /** Text to copy to the clipboard before the accept goes out. */
  copy: string | null
  word: AcceptedWord
}

const WORD: Record<MailProposalKind, AcceptedWord> = {
  calendar_block: 'Added',
  reminder: 'Added',
  waiting_on: 'Added',
  mark_done: 'Done',
  open_link: 'Opened',
  draft_reply: 'Copied',
  verify_official: 'Noted',
  already_in_lifeos: 'Noted',
}

export function acceptedWord(kind: MailProposalKind): AcceptedWord {
  return WORD[kind]
}

const ACCEPT_LABEL: Record<MailProposalKind, string> = {
  calendar_block: 'Add to calendar',
  reminder: 'Set reminder',
  waiting_on: 'Set reminder',
  open_link: 'Open link',
  mark_done: 'Mark done',
  draft_reply: 'Copy draft',
  verify_official: 'Got it',
  already_in_lifeos: 'Got it',
}

/** The Accept button's verb, per kind: the row says what pressing it does. */
export function acceptLabel(kind: MailProposalKind): string {
  return ACCEPT_LABEL[kind]
}

/** What Accept does, per kind. Every kind calls accept; this is what happens around it. */
export function acceptPlan(p: MailProposal): AcceptPlan {
  const base: AcceptPlan = { toast: null, undo: false, leaves: false, open: null, copy: null, word: WORD[p.kind] }
  const pl = p.payload
  switch (p.kind) {
    case 'calendar_block':
      return { ...base, toast: 'Added to calendar', undo: true }
    case 'reminder':
    case 'waiting_on':
      return { ...base, toast: 'Reminder set', undo: true }
    case 'mark_done':
      return { ...base, toast: 'Done', undo: true, leaves: true }
    case 'open_link':
      return { ...base, open: 'url' in pl ? safeHttpUrl(pl.url) : null }
    case 'draft_reply':
      return { ...base, copy: 'text' in pl ? pl.text : null }
    default:
      return base
  }
}

/**
 * Only http(s) leaves the page. A model-written URL is data: a `javascript:` or `data:` link is not
 * opened, and the row offers Skip only.
 */
export function safeHttpUrl(url: string): string | null {
  try {
    const u = new URL(url)
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null
  } catch {
    return null
  }
}

/** Can this row be accepted at all? (An open_link with an unsafe URL cannot.) */
export function canAccept(p: MailProposal): boolean {
  if (p.kind === 'open_link') return acceptPlan(p).open !== null
  if (p.kind === 'draft_reply') return acceptPlan(p).copy !== null
  return true
}

/** Proposed first, then accepted, then skipped — each in the API's newest-first order. */
export function groupProposals(list: MailProposal[]): { proposed: MailProposal[]; accepted: MailProposal[]; skipped: MailProposal[] } {
  return {
    proposed: list.filter((p) => p.status === 'proposed'),
    accepted: list.filter((p) => p.status === 'accepted'),
    skipped: list.filter((p) => p.status === 'skipped'),
  }
}

/** The button's badge: proposals still waiting on a decision. 0 = no badge. */
export function proposedCount(mail: Pick<MailTriageItem, 'proposals'>): number {
  return (mail.proposals ?? []).filter((p) => p.status === 'proposed').length
}

/** The panel's empty states, exact copy. Null when there are rows to show. */
export function emptyLine(mail: Pick<MailTriageItem, 'proposals' | 'proposalsAt'>): string | null {
  if (!mail.proposalsAt) return 'Next steps not ready yet'
  if ((mail.proposals ?? []).length === 0) return 'Nothing to propose'
  return null
}

/** '6:42' today · 'yesterday at 22:14' · 'Sep 14 at 22:14' — the "sorted by Claude" line's grammar. */
export function proposedAtWords(ts: string, today: ISODate): string {
  const day = localDayOf(ts)
  if (day === today) return clock24(ts)
  if (day === addDays(today, -1)) return `yesterday at ${clock24(ts)}`
  return `${formatShortDate(day)} at ${clock24(ts)}`
}

/**
 * The panel foot, exact copy, one line per source the proposals came from (newest of each).
 * Nothing proposed → no line: there is no claim to attribute.
 */
export function provenanceLines(list: MailProposal[], today: ISODate): string[] {
  const out: string[] = []
  for (const source of ['body', 'snippet'] as const) {
    const of = list.filter((p) => p.source === source)
    if (of.length === 0) continue
    const at = of.map((p) => p.createdAt).sort().at(-1)!
    out.push(
      source === 'body'
        ? `Proposed by Claude from the full message · read, not stored · ${proposedAtWords(at, today)}`
        : `Proposed by Claude from the subject and snippet · ${proposedAtWords(at, today)}`,
    )
  }
  return out
}

/** What Accept would write, from the payload: 'Sat Sep 19, 7:00 PM–9:00 PM · Ramen place' · 'due Fri Sep 18'. */
export function payloadLine(p: MailProposal): string | null {
  const pl = p.payload
  if (p.kind === 'calendar_block' && 'startAt' in pl) {
    const when = `${dayLabel(localDayOf(pl.startAt))}, ${clock12(pl.startAt)}–${clock12(pl.endAt)}`
    return pl.location ? `${when} · ${pl.location}` : when
  }
  if ((p.kind === 'reminder' || p.kind === 'waiting_on') && 'dueDate' in pl) return `due ${dayLabel(pl.dueDate as ISODate)}`
  if (p.kind === 'verify_official' && 'step' in pl) return pl.step
  if (p.kind === 'mark_done' && 'reason' in pl) return pl.reason
  return null
}

/**
 * One sentence saying exactly what Accept does for this row, with the payload's values filled in.
 * Exact copy by owner decision (2026-10-07). `_today` is part of the signature for the row's
 * grammar; dates are printed absolute, so it is not read yet.
 */
export function whatHappens(p: MailProposal, _today: ISODate): string {
  const pl = p.payload
  const due = 'dueDate' in pl ? dayLabel(pl.dueDate as ISODate) : ''
  switch (p.kind) {
    case 'calendar_block':
      return `Adds a block to your LifeOS calendar: ${payloadLine(p) ?? ''}. Nothing goes to Google Calendar. Undo for 5 seconds.`
    case 'reminder':
      return `Sets a reminder due ${due}. Undo for 5 seconds.`
    case 'waiting_on':
      return `Sets a reminder to nudge if no reply by ${due}. Undo for 5 seconds.`
    case 'open_link': {
      const href = 'url' in pl ? safeHttpUrl(pl.url) : null
      return href ? `Opens ${new URL(href).hostname} in a new tab. LifeOS cannot act there for you.` : 'This link cannot be opened from here.'
    }
    case 'mark_done':
      return 'Marks this mail done and it leaves the pile. Undo for 5 seconds.'
    case 'draft_reply':
      return 'Copies the draft. You paste and send it in Gmail; LifeOS never sends mail.'
    case 'verify_official':
      return 'Nothing is written. You do this step yourself in the official app.'
    case 'already_in_lifeos':
      return `Nothing to add. It is already in LifeOS: ${'title' in pl ? pl.title : p.title}.`
  }
}

/** already_in_lifeos: the words, and the page that holds it where there is one. */
export function alreadyIn(p: MailProposal): { text: string; href: string | null } | null {
  const pl = p.payload
  if (p.kind !== 'already_in_lifeos' || !('what' in pl)) return null
  const href = pl.what === 'calendar_block' ? '/calendar' : pl.what === 'task' ? '/tasks' : null
  return { text: `Already in LifeOS: ${pl.title}`, href }
}

// ── Cache patch (the optimistic half; the triage is re-read after every decision) ──

export function patchProposal(t: MailTriage, proposalId: string, patch: Partial<MailProposal>): MailTriage {
  const fix = (list: MailTriageItem[] | undefined) =>
    list?.map((m) =>
      (m.proposals ?? []).some((p) => p.id === proposalId)
        ? { ...m, proposals: m.proposals.map((p) => (p.id === proposalId ? { ...p, ...patch } : p)) }
        : m,
    )
  const g = t.groups
  return {
    ...t,
    groups: {
      ...g,
      needsYouToday: fix(g.needsYouToday)!,
      important: fix(g.important)!,
      canWait: fix(g.canWait)!,
      noise: fix(g.noise)!,
      unsorted: fix(g.unsorted)!,
      ...(g.snoozed ? { snoozed: fix(g.snoozed) } : {}),
    },
  }
}

/** N toggles the Next steps panel — on a focused MAIL card only (reminders and assignments have none). */
export function isStepsKey(key: string, kind: 'mail' | 'assignment' | 'reminder'): boolean {
  return key.toLowerCase() === 'n' && kind === 'mail'
}
